/**
 * Order keys: strings that sort siblings in a map of schema v2
 * (docs/plans/2026-10-02-crdt-schema-v2.md). A node's place among its
 * siblings is its `order` key; inserting between two siblings writes one key,
 * on the inserted node only, so two concurrent inserts never overwrite each
 * other (equal keys from inserts at the same place sort by node id).
 *
 * Fractional indexing (after David Greenspan's "Implementing Fractional
 * Indexing" and the rocicorp `fractional-indexing` package, CC0): a key is an
 * integer part — a letter giving its length ('a'..'z' positive, 'A'..'Z'
 * negative) followed by base-62 digits — then an optional fraction with no
 * trailing '0'. Appending at the end or the start grows keys logarithmically,
 * not linearly. Keys compare with plain `<` (ASCII order of the digits).
 */

const DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const INTEGER_ZERO = "a0";
// The smallest integer: 'A' followed by 26 zero digits. Never produced.
const SMALLEST_INTEGER = "A" + "0".repeat(26);

function integerLength(head) {
  if (head >= "a" && head <= "z") return head.charCodeAt(0) - "a".charCodeAt(0) + 2;
  if (head >= "A" && head <= "Z") return "Z".charCodeAt(0) - head.charCodeAt(0) + 2;
  throw new Error(`Invalid order key head: ${head}`);
}

function integerPart(key) {
  const length = integerLength(key[0]);
  if (length > key.length) throw new Error(`Invalid order key: ${key}`);
  return key.slice(0, length);
}

/** Throws on a string that is not an order key (the CRDT can hold anything). */
export function validateOrderKey(key) {
  if (typeof key !== "string" || !key) throw new Error(`Invalid order key: ${key}`);
  if (key === SMALLEST_INTEGER) throw new Error(`Invalid order key: ${key}`);
  const integer = integerPart(key);
  for (const char of key.slice(1)) {
    if (!DIGITS.includes(char)) throw new Error(`Invalid order key: ${key}`);
  }
  if (key.slice(integer.length).endsWith("0")) throw new Error(`Invalid order key: ${key}`);
}

export function isValidOrderKey(key) {
  try { validateOrderKey(key); return true; } catch { return false; }
}

/** A fraction strictly between a and b ('' = 0, null = 1), no trailing '0'. */
function midpoint(a, b) {
  if (b !== null && a >= b) throw new Error(`${a} >= ${b}`);
  if (a.endsWith("0") || (b && b.endsWith("0"))) throw new Error("trailing zero");
  if (b) {
    // Skip the common prefix (a padded with zeros).
    let n = 0;
    while ((a[n] || "0") === b[n]) n++;
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
  }
  const digitA = a ? DIGITS.indexOf(a[0]) : 0;
  const digitB = b !== null ? DIGITS.indexOf(b[0]) : DIGITS.length;
  if (digitB - digitA > 1) return DIGITS[Math.round(0.5 * (digitA + digitB))];
  // Consecutive first digits.
  if (b && b.length > 1) return b.slice(0, 1);
  return DIGITS[digitA] + midpoint(a.slice(1), null);
}

function incrementInteger(x) {
  const [head, ...digits] = x.split("");
  let carry = true;
  for (let i = digits.length - 1; carry && i >= 0; i--) {
    const d = DIGITS.indexOf(digits[i]) + 1;
    if (d === DIGITS.length) digits[i] = "0";
    else { digits[i] = DIGITS[d]; carry = false; }
  }
  if (!carry) return head + digits.join("");
  if (head === "Z") return "a0";
  if (head === "z") return null; // the largest integer: never reached in practice
  const nextHead = String.fromCharCode(head.charCodeAt(0) + 1);
  if (nextHead > "a") digits.push("0");
  else digits.pop();
  return nextHead + digits.join("");
}

function decrementInteger(x) {
  const [head, ...digits] = x.split("");
  let borrow = true;
  for (let i = digits.length - 1; borrow && i >= 0; i--) {
    const d = DIGITS.indexOf(digits[i]) - 1;
    if (d === -1) digits[i] = DIGITS[DIGITS.length - 1];
    else { digits[i] = DIGITS[d]; borrow = false; }
  }
  if (!borrow) return head + digits.join("");
  if (head === "a") return "Z" + DIGITS[DIGITS.length - 1];
  if (head === "A") return null; // the smallest integer: never reached in practice
  const nextHead = String.fromCharCode(head.charCodeAt(0) - 1);
  if (nextHead < "Z") digits.push(DIGITS[DIGITS.length - 1]);
  else digits.pop();
  return nextHead + digits.join("");
}

/**
 * A key strictly between a and b (null = no bound on that side).
 * @param {string|null} a
 * @param {string|null} b
 */
export function keyBetween(a, b) {
  if (a !== null) validateOrderKey(a);
  if (b !== null) validateOrderKey(b);
  if (a !== null && b !== null && a >= b) throw new Error(`${a} >= ${b}`);
  if (a === null) {
    if (b === null) return INTEGER_ZERO;
    const intB = integerPart(b);
    const fracB = b.slice(intB.length);
    if (intB === SMALLEST_INTEGER) return intB + midpoint("", fracB);
    if (intB < b) return intB;
    const before = decrementInteger(intB);
    if (before === null) throw new Error("Cannot decrement any more");
    return before;
  }
  if (b === null) {
    const intA = integerPart(a);
    const fracA = a.slice(intA.length);
    const after = incrementInteger(intA);
    return after === null ? intA + midpoint(fracA, null) : after;
  }
  const intA = integerPart(a);
  const fracA = a.slice(intA.length);
  const intB = integerPart(b);
  const fracB = b.slice(intB.length);
  if (intA === intB) return intA + midpoint(fracA, fracB);
  const after = incrementInteger(intA);
  if (after === null) throw new Error("Cannot increment any more");
  if (after < b) return after;
  return intA + midpoint(fracA, null);
}

/**
 * n keys in increasing order, strictly between a and b.
 * @returns {string[]}
 */
export function keysBetween(a, b, n) {
  if (n === 0) return [];
  if (n === 1) return [keyBetween(a, b)];
  if (b === null) {
    const keys = [];
    let current = keyBetween(a, b);
    keys.push(current);
    for (let i = 1; i < n; i++) { current = keyBetween(current, b); keys.push(current); }
    return keys;
  }
  if (a === null) {
    const keys = [];
    let current = keyBetween(a, b);
    keys.push(current);
    for (let i = 1; i < n; i++) { current = keyBetween(a, current); keys.push(current); }
    return keys.reverse();
  }
  const mid = Math.floor(n / 2);
  const c = keyBetween(a, b);
  return [...keysBetween(a, c, mid), c, ...keysBetween(c, b, n - mid - 1)];
}

/** Sort comparator for siblings: by order key, then by id (equal keys from concurrent inserts). */
export function compareSiblings(a, b) {
  if (a.order !== b.order) return a.order < b.order ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
